import React, { useState } from 'react';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { StickyNote, X, Check } from 'lucide-react';
import { api } from '../services/api';
import { NotePhotoStrip, AddNotePhotoButton, NotePhoto, EnqueueOffline } from './NotePhotos';

/**
 * The note the crew reads before starting a job — editable by the office from
 * the route, and by the crew member standing in the yard.
 *
 * "Just this visit" writes the job's own note. "Every visit from now on" writes
 * the property's standing note too, so it survives into visits generated later.
 * This is NOT the admin-only Internal Notes field.
 */
// The lawn plan's block on a mow (services/planSeason.noteBlock): the plan's lines, not the crew's words. The editor
// shows it apart and never sends it back, so treatment notes and crew notes stay separate (owner, Oct 20 2026).
export const PLAN_MARK = '🌱 LAWN PLAN ON THIS MOW';
export function splitPlanBlock(text: string | null | undefined): { block: string | null; own: string } {
  const block: string[] = [], own: string[] = [];
  let inBlock = false;
  for (const line of String(text || '').split('\n')) {
    if (line.startsWith(PLAN_MARK)) { inBlock = true; block.push(line); continue; }
    if (inBlock && line.startsWith('• ')) { block.push(line); continue; }
    inBlock = false;
    own.push(line);
  }
  return { block: block.length ? block.join('\n') : null, own: own.join('\n').replace(/^\n+/, '').replace(/\n{3,}/g, '\n\n').trimEnd() };
}

export default function CrewNoteEditor({
  jobId, notes, onSaved, variant = 'admin', autoOpen = false, onClose, photos, onPhotosChanged, enqueueOffline,
}: {
  jobId: number;
  notes: string | null | undefined;
  onSaved?: (notes: string | null) => void;
  variant?: 'admin' | 'crew';
  autoOpen?: boolean;
  onClose?: () => void;
  photos?: NotePhoto[] | null;          // photos already on the note
  onPhotosChanged?: () => void;         // a photo was added or removed (they save on their own)
  enqueueOffline?: EnqueueOffline;
}) {
  const [open, setOpen] = useState(autoOpen);
  const parts = splitPlanBlock(notes);
  const [text, setText] = useState(parts.own);
  const [scope, setScope] = useState<'this' | 'future'>('this');
  const crew = variant === 'crew';
  const own = parts.own;   // the crew's words alone — what the button and the box are about

  const save = useMutation(
    () => api.post(`/jobs/${jobId}/crew-notes`, { notes: text, scope }),
    {
      onSuccess: (r: any) => {
        toast.success(r.data?.message || 'Saved');
        // The whole note the visit now carries (the plan block first, when there is one), as the server has it.
        onSaved?.(r.data?.notes !== undefined ? (r.data.notes || null) : (text.trim() || null));
        setOpen(false);
        onClose?.();
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save the note'); },
    },
  );

  function close() { setText(own); setOpen(false); onClose?.(); }

  if (!open) {
    return (
      <button
        onClick={e => { e.stopPropagation(); setText(own); setOpen(true); }}
        className={crew
          ? 'text-xs font-bold underline text-amber-950/80'
          : 'text-[11px] px-1.5 py-0.5 rounded-full border font-medium flex items-center gap-1 ' +
            (own ? 'border-orange-300 bg-orange-50 text-orange-800' : 'border-gray-200 text-gray-500 hover:border-orange-300 hover:text-orange-700')}
        title={own ? `Crew note: ${own}` : 'Add a note for the crew'}
      >
        {crew ? (own ? 'Edit note' : 'Add a note') : (<><StickyNote className="h-3 w-3" />{own ? 'note' : 'add note'}</>)}
      </button>
    );
  }

  return (
    <div className={`w-full rounded-lg p-2 space-y-2 ${crew ? 'bg-amber-100 border border-amber-500' : 'bg-orange-50 border border-orange-200'}`} onClick={e => e.stopPropagation()}>
      {parts.block && (
        <div className="rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-800">Lawn plan on this mow — set by the plan, not part of this note</p>
          <pre className="mt-0.5 whitespace-pre-wrap font-sans text-xs text-emerald-900">{parts.block}</pre>
          {!crew && <p className="text-[10px] text-emerald-700 mt-0.5">It stays on this visit whatever you write below. Change the treatment on the customer's plan, not here.</p>}
        </div>
      )}
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        rows={2}
        autoFocus
        placeholder="What the crew needs to know — weed eat only, dog in back, gate code 1234…"
        className={`w-full rounded-md px-2 py-1.5 text-sm ${crew ? 'bg-white text-gray-900 border border-amber-400' : 'border border-orange-200'}`}
      />
      {photos && photos.length > 0 && (
        <NotePhotoStrip photos={photos} size="sm" canRemove={crew ? (p => !!p.mine) : true} onRemoved={onPhotosChanged} />
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        {(['this', 'future'] as const).map(s => (
          <button key={s} onClick={() => setScope(s)}
            className={`press text-[11px] px-2 py-1 rounded-full border font-medium ${scope === s ? 'bg-brand-600 text-black border-brand-600' : (crew ? 'bg-white text-gray-700 border-amber-400' : 'bg-white text-gray-600 border-gray-300')}`}>
            {s === 'this' ? 'Just this visit' : 'Every visit from now on'}
          </button>
        ))}
        <AddNotePhotoButton jobId={jobId} scope={scope} label="Add a photo" onAdded={onPhotosChanged} enqueueOffline={enqueueOffline}
          className={`text-[11px] px-2 py-1 rounded-full border font-medium ${crew ? 'bg-white text-amber-900 border-amber-500' : 'bg-white text-gray-700 border-gray-300'}`} />
        <div className="ml-auto flex items-center gap-1">
          <button onClick={close} className="p-1.5 rounded-md text-gray-500 hover:text-gray-800" title="Cancel"><X className="h-4 w-4" /></button>
          <button onClick={() => save.mutate()} disabled={save.isLoading}
            className="press text-xs px-2.5 py-1.5 rounded-xl bg-brand-600 text-black font-semibold flex items-center gap-1 disabled:opacity-50">
            <Check className="h-3.5 w-3.5" /> {save.isLoading ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
